import { useCallback, useEffect, useState } from "react";
import { Gavel, Pencil, History, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MILESTONES, gbp, ukDate, ukDateTime } from "./crmPipelineShared";
import { LoadError, errMsg } from "./LoadError";
import { DateField, localToday } from "./DateField";
import { useStageMover } from "./useStageMover";
import { REFERRAL_STAGES, VENDOR_OUTCOMES } from "./crmPipelineShared";

const field = "w-full bg-[#091E16] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white outline-none focus:border-brass";
const DATE_FIELDS = [...MILESTONES.map(([, f, l]) => [f, l]), ["earned_date", "Commission earned"], ["invoice_date", "Invoice issued"], ["payment_due_date", "Payment due"], ["next_follow_up", "Next follow-up"]];
const NUM_FIELDS = [["asking_price", "Asking price (£)"], ["sale_price", "Sale price (£)"], ["expected_commission_override", "Expected commission override (£)"], ["fee_earned", "Fee earned (£, if different)"], ["invoice_amount", "Invoice amount (£)"], ["received_commission", "Commission received (£)"]];
const TEXT_FIELDS = [["provider", "Auction partner"], ["auction_contact", "Auction contact"], ["property_address", "Property address"], ["invoice_number", "Invoice number"]];

function EditForm({ r, onDone }) {
  const init = {};
  [...DATE_FIELDS, ...TEXT_FIELDS].forEach(([k]) => { init[k] = r[k] || ""; });
  NUM_FIELDS.forEach(([k]) => { init[k] = r[k] ?? ""; });
  const [f, setF] = useState({ ...init, correction_reason: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const body = { correction_reason: f.correction_reason };
    Object.keys(init).forEach((k) => { if (String(f[k]) !== String(init[k])) body[k] = NUM_FIELDS.some(([n]) => n === k) ? (f[k] === "" ? null : parseFloat(f[k])) : f[k]; });
    setBusy(true);
    try { await api.put(`/crm/auction-referrals/${r.id}`, body); toast.success("Referral corrected — change logged"); onDone(); }
    catch (e) { toast.error(errMsg(e, "Could not save")); } finally { setBusy(false); }
  };
  const inp = ([k, l], type) => (
    <div key={k}><label className="block text-[11px] text-emerald-100/50 mb-0.5">{l}</label>
      {type === "date" ? <DateField value={f[k]} onChange={(v) => setF((x) => ({ ...x, [k]: v }))} testId={`ref-edit-${k}`} placeholder="—" />
        : <input type={type} value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} data-testid={`ref-edit-${k}`} className={field} />}</div>
  );
  return (
    <div className="mt-3 space-y-3 border-t border-white/10 pt-3" data-testid="ref-edit-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">{TEXT_FIELDS.map((x) => inp(x, "text"))}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{DATE_FIELDS.map((x) => inp(x, "date"))}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{NUM_FIELDS.map((x) => inp(x, "number"))}</div>
      {inp(["correction_reason", "Reason for correction (kept in audit trail)"], "text")}
      <button onClick={save} disabled={busy} data-testid="ref-edit-save" className="text-sm bg-brass text-emerald-950 font-semibold rounded-lg px-4 py-1.5">{busy ? "Saving…" : "Save corrections"}</button>
    </div>
  );
}

function SeparateStage({ r, onChanged }) {
  const mover = useStageMover({ view: "vendor", onMoved: onChanged, referralId: r.id });
  return (
    <>
      {mover.dialog}
      <select value={r.voided ? "" : r.pipeline_stage} onChange={(e) => mover.request({ store: r.vendor_store, id: r.vendor_id, hasReferral: true }, e.target.value)}
        data-testid={`ref-stage-select-${r.id}`} className="mt-2 bg-[#091E16] border border-white/10 rounded-md px-2 py-1 text-[11px] text-emerald-100/80">
        {[...REFERRAL_STAGES, ...VENDOR_OUTCOMES.slice(0, 3)].map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </>
  );
}

function ReferralCard({ r, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [hist, setHist] = useState(false);
  return (
    <div className={`rounded-lg border p-3 ${r.voided ? "border-white/5 opacity-60" : "border-white/10 bg-white/[0.03]"}`} data-testid={`referral-${r.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm text-white font-medium">{r.provider} · {r.property_address || "—"}</div>
          <div className="text-[11px] text-emerald-100/50">{r.separate ? "Separate property · " : "Main referral (follows the record's stage) · "}{r.auction_contact ? `Contact: ${r.auction_contact} · ` : ""}Stage: {r.pipeline_stage || r.status}{r.original_source ? ` · Source: ${r.original_source}` : ""}</div>
          {r.separate && <SeparateStage r={r} onChanged={onChanged} />}
        </div>
        <div className="flex gap-1">
          {r.voided ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-emerald-100/60" data-testid="ref-voided">Voided (not counted)</span>
            : <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-200" data-testid="ref-counted">Referral conversion ✓</span>}
          <button onClick={() => setEdit((x) => !x)} data-testid={`ref-edit-toggle-${r.id}`} className="p-1 text-emerald-100/50 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={() => setHist((x) => !x)} data-testid={`ref-history-toggle-${r.id}`} className="p-1 text-emerald-100/50 hover:text-white"><History className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {r.referral_date_missing && <div className="mt-1.5 text-[11px] text-amber-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Referral date not recorded — add it via edit</div>}
      <ol className="mt-2.5 grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
        {MILESTONES.map(([, f, l]) => (
          <li key={f} className="text-[11px]"><span className={r[f] ? "text-emerald-300" : "text-emerald-100/30"}>{r[f] ? "●" : "○"} {l}</span><div className="text-emerald-100/60 pl-3">{r[f] ? ukDate(r[f]) : "—"}</div></li>
        ))}
      </ol>
      <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 text-[11px]" data-testid="ref-money">
        <span className="text-emerald-100/60">Expected (forecast): <b className="text-white font-mono">{r.expected_commission != null ? gbp(r.expected_commission) : "—"}</b></span>
        <span className="text-emerald-100/60">Earned: <b className="text-white font-mono">{r.earned_date ? `${gbp(r.fee_earned ?? r.expected_commission)} · ${ukDate(r.earned_date)}` : "Not yet"}</b></span>
        <span className="text-emerald-100/60" data-testid="ref-invoiced">Invoiced: <b className="text-white font-mono">{r.invoice_date ? `${gbp(r.invoice_amount ?? r.fee_earned ?? r.expected_commission)} · ${ukDate(r.invoice_date)}${r.invoice_number ? ` · #${r.invoice_number}` : ""}${r.payment_due_date ? ` · due ${ukDate(r.payment_due_date)}` : ""}` : "Not yet"}</b></span>
        <span className="text-emerald-100/60">Received (actual): <b className="text-emerald-300 font-mono">{r.commission_received_date ? gbp(r.received_commission) : "Not yet"}</b></span>
      </div>
      {edit && <EditForm r={r} onDone={() => { setEdit(false); onChanged(); }} />}
      {hist && (
        <ul className="mt-3 border-t border-white/10 pt-2 space-y-1.5" data-testid="ref-history">
          {[...(r.history || [])].reverse().map((h, i) => (
            <li key={i} className="text-[11px] text-emerald-100/60"><span className="text-white">{h.action}</span>{h.reason ? ` — ${h.reason}` : ""} · {ukDateTime(h.at)} · {h.by}
              {h.changes && Object.keys(h.changes).length > 0 && <div className="pl-2 text-emerald-100/40">{Object.entries(h.changes).map(([k, [a, b]]) => `${k}: ${a === "" || a == null ? "—" : a} → ${b === "" || b == null ? "—" : b}`).join("; ")}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewReferral({ store, id, onDone }) {
  const [f, setF] = useState({ property_address: "", referral_date: localToday(), partner: "Pattinsons", auction_contact: "", asking_price: "" });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (!f.property_address.trim()) return toast.error("Enter the property address for this separate referral");
    try { await api.post("/crm/auction-referrals", { store, id, ...f, asking_price: f.asking_price ? parseFloat(f.asking_price) : null }); toast.success("Separate referral recorded"); onDone(); }
    catch (e) { toast.error(errMsg(e, "Could not create referral")); }
  };
  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 border-t border-white/10 pt-3" data-testid="ref-new-form">
      <input placeholder="Property address *" value={f.property_address} onChange={set("property_address")} data-testid="ref-new-address" className={field} />
      <DateField value={f.referral_date} onChange={(v) => setF((x) => ({ ...x, referral_date: v }))} testId="ref-new-date" />
      <input placeholder="Auction partner" value={f.partner} onChange={set("partner")} className={field} />
      <input type="number" placeholder="Asking price (£)" value={f.asking_price} onChange={set("asking_price")} className={field} />
      <button onClick={save} data-testid="ref-new-save" className="text-sm bg-brass text-emerald-950 font-semibold rounded-lg px-4 py-1.5 sm:col-span-2">Record separate referral</button>
    </div>
  );
}

export function ReferralPanel({ store, id, refreshKey, onLoaded }) {
  const [refs, setRefs] = useState(null);
  const [err, setErr] = useState(false);
  const [adding, setAdding] = useState(false);
  const load = useCallback(async () => {
    setErr(false);
    try { const { data } = await api.get("/crm/auction-referrals", { params: { store, id } }); setRefs(data.referrals); onLoaded && onLoaded(data.referrals); }
    catch (e) { setErr(true); }
  }, [store, id, onLoaded]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return (
    <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="referral-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Gavel className="w-4 h-4 text-brass" /><h3 className="text-sm font-semibold text-white">Auction referrals</h3></div>
        <button onClick={() => setAdding((x) => !x)} data-testid="ref-new-toggle" className="text-[11px] flex items-center gap-1 text-brass hover:underline"><Plus className="w-3 h-3" /> Separate property referral</button>
      </div>
      {err && <LoadError message="Referrals could not be loaded." onRetry={load} testId="ref-error" />}
      {refs && refs.length === 0 && <p className="text-xs text-emerald-100/40" data-testid="ref-empty">No auction referral yet — move the stage to "Referred to Auction Partner" to record one.</p>}
      <div className="space-y-2.5">{(refs || []).map((r) => <ReferralCard key={r.id} r={r} onChanged={load} />)}</div>
      {adding && <NewReferral store={store} id={id} onDone={() => { setAdding(false); load(); }} />}
    </div>
  );
}
